import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'localization';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import type { StatusValue } from 'types/spherePoints';
import AtlasSphereIdentityMolecule from '../AtlasSphereIdentity/AtlasSphereIdentityMolecule';
import StatusBadgeAtom from '../../../atoms/status-badge/StatusBadgeAtom';
import { getStatusColor } from '../../../../utils/statusColors';
import CheckInFormContent from '../../StatusCheckInDialog/CheckInFormContent';
import type { CheckInHistoryItem } from '../../../../types/checkIns';
import { VELOCITY_BUCKET_LABEL_KEYS } from '../../StatusCheckInDialog/checkInConstants';

export type { StatusValue } from 'types/spherePoints';
export type { CheckInHistoryItem } from '../../../../types/checkIns';

interface StatusOption {
  value: StatusValue;
  label: string;
  color: string;
  icon: string;
}

export interface FactorData {
  id: string;
  factorType: string;
  name: string;
  description: string;
  weight: number;
  status: string;
  statusLabel: string;
  /**
   * Underlying canonical health status independent of lifecycle overlay.
   * When set, callers rendering the "why this color?" chip should prefer
   * this over `status` (which may be a lifecycle mode like 'observing').
   */
  currentStatus?: StatusValue;
  velocityScore?: number;
  velocityBucket?: VelocityBucketKey;
  commentRequired?: boolean;
  minCommentLength?: number;
  /**
   * When materialization detects the factor's rrule cannot be expanded
   * (parse error, unresolvable timezone, contradictory constraints), it
   * writes `factor.metadata.scheduleBroken` on the graph vertex. The
   * FactorSummaryMolecule reads it and renders a broken-schedule badge
   * so the sphere owner is not left with a factor that silently never
   * fires. See EAINT-746.
   */
  scheduleBroken?: {
    code: string;
    message: string;
    detectedAt: string;
  };
}

/** API client interface - minimal shape needed */
interface ApiClients {
  graphClient?: {
    get: (url: string) => Promise<any>;
  };
}

/** Panel mode - determines initial view */
export type PanelMode = 'history' | 'checkin';

export interface StatusFactorPanelProps {
  factor: FactorData;
  sphereId: string;
  hasEditPermission: boolean;
  loading: boolean;
  onClose: () => void;
  onCheckInSubmit: (statusValue: StatusValue, comment?: string) => void;
  apiClients?: ApiClients | null;
  /** Initial mode: 'history' shows history with check-in button, 'checkin' shows form directly */
  mode?: PanelMode;
}

const StatusFactorPanel: React.FC<StatusFactorPanelProps> = ({
  factor,
  sphereId,
  hasEditPermission,
  loading,
  onClose,
  onCheckInSubmit,
  apiClients,
  mode = 'history',
}) => {
  const { t } = useTranslation();

  // Current view state - can switch between history and checkin
  const [currentView, setCurrentView] = useState<PanelMode>(mode);

  // Check-in history state
  const [checkInHistory, setCheckInHistory] = useState<CheckInHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const isManual = factor.factorType === 'manual';

  // Status options with colors from centralized config (used only for history rendering)
  const statusOptions: StatusOption[] = [
    {
      value: 'on_track',
      label: t('statusPopover.checkIn.statusOnTrack', 'On Track'),
      color: getStatusColor('on_track'),
      icon: 'pi pi-check-circle',
    },
    {
      value: 'at_risk',
      label: t('statusPopover.checkIn.statusAtRisk', 'At Risk'),
      color: getStatusColor('at_risk'),
      icon: 'pi pi-exclamation-triangle',
    },
    {
      value: 'needs_attention',
      label: t('statusPopover.checkIn.statusNeedsAttention', 'Needs Attention'),
      color: getStatusColor('needs_attention'),
      icon: 'pi pi-bell',
    },
    {
      value: 'critical',
      label: t('statusPopover.checkIn.statusCritical', 'Critical'),
      color: getStatusColor('critical'),
      icon: 'pi pi-times-circle',
    },
    {
      value: 'blocked',
      label: t('statusPopover.checkIn.statusBlocked', 'Blocked'),
      color: getStatusColor('blocked'),
      icon: 'pi pi-ban',
    },
  ];

  // Fetch check-in history for manual factors
  const fetchCheckInHistory = useCallback(async () => {
    if (!isManual || !apiClients?.graphClient || !sphereId || !factor.id) {
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      // graphClient.get returns data directly (via extract())
      const data = await apiClients.graphClient.get(
        `/sphere/${sphereId}/factors/${factor.id}/checkins?limit=10&activeOnly=true`
      );

      if (data?.checkIns) {
        setCheckInHistory(data.checkIns);
      } else {
        setCheckInHistory([]);
      }
    } catch (err: any) {
      setHistoryError(err.message || 'Failed to load history');
      setCheckInHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [isManual, apiClients, sphereId, factor.id]);

  // Reset view and history when factor changes or mode changes
  useEffect(() => {
    setCheckInHistory([]);
    setHistoryError(null);
    setCurrentView(mode);

    // Fetch history for manual factors when in history mode
    if (isManual && mode === 'history') {
      fetchCheckInHistory();
    }
  }, [factor.id, mode, isManual, fetchCheckInHistory]);

  // Format relative time for history items
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('time.justNow', 'just now');
    if (diffMins < 60) return t('time.minutesAgo', '{{count}} min ago', { count: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', '{{count}}h ago', { count: diffHours });
    if (diffDays < 7) return t('time.daysAgo', '{{count}}d ago', { count: diffDays });

    // For older dates, show the actual date
    return date.toLocaleDateString();
  };

  // Prevent clicks and keyboard events inside panel from closing the popover
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    // Stop all keyboard events from bubbling up to prevent popover from closing
    e.stopPropagation();
  };

  // Handle switching to check-in view
  const handleSwitchToCheckIn = () => {
    setCurrentView('checkin');
  };

  // Handle back from check-in to history (only in history mode)
  const handleBackToHistory = () => {
    setCurrentView('history');
  };

  // Determine panel title based on view
  const getPanelTitle = () => {
    if (currentView === 'checkin') {
      return t('statusPopover.panel.checkInTitle', 'Check-In: {{name}}', { name: factor.name });
    }
    return t('statusPopover.panel.historyTitle', '{{name}}', { name: factor.name });
  };

  // Handle back button - either close panel or go back to history
  const handleBack = () => {
    if (currentView === 'checkin' && mode === 'history') {
      // If we switched to checkin from history, go back to history
      handleBackToHistory();
    } else {
      // Otherwise close the panel
      onClose();
    }
  };

  // Get status option for history items
  const getStatusOption = (statusValue: StatusValue): StatusOption | undefined => {
    return statusOptions.find((o) => o.value === statusValue);
  };

  return (
    <div className="status-factor-panel" role="region" onClick={handlePanelClick} onKeyDown={handlePanelKeyDown}>
      {/* Panel Header */}
      <div className="status-factor-panel__header">
        <button
          className="status-factor-panel__back-btn"
          onClick={handleBack}
          disabled={loading}
          title={t('common.back', 'Back')}
        >
          <i className="pi pi-arrow-left" />
        </button>
        <h4 className="status-factor-panel__title">
          {getPanelTitle()}
        </h4>
        {factor.velocityBucket && (
          <span
            className="status-factor-panel__velocity"
            data-testid="status-factor-panel-velocity"
          >
            {t(VELOCITY_BUCKET_LABEL_KEYS[factor.velocityBucket] || 'velocity.buckets.stable')}
            {factor.velocityScore !== undefined && (
              <span className="status-factor-panel__velocity-score">
                {' '}({factor.velocityScore > 0 ? '+' : ''}{factor.velocityScore})
              </span>
            )}
          </span>
        )}
      </div>

      {/* Panel Content */}
      <div className="status-factor-panel__content">
        {/* CHECK-IN VIEW: Canonical CheckInFormContent */}
        {currentView === 'checkin' && isManual && hasEditPermission && (
          <CheckInFormContent
            commentRequired={factor.commentRequired}
            minCommentLength={factor.minCommentLength}
            velocityScore={factor.velocityScore}
            velocityBucket={factor.velocityBucket}
            defaultStatus={factor.status as StatusValue}
            isSubmitting={loading}
            onSubmit={onCheckInSubmit}
            onCancel={handleBackToHistory}
            className="status-factor-panel__checkin-form"
          />
        )}

        {/* HISTORY VIEW: History list with check-in button */}
        {currentView === 'history' && isManual && (
          <div className="status-factor-panel__history">
            {/* Check-in button at top (if user has permission) */}
            {hasEditPermission && (
              <button
                className="status-factor-panel__checkin-btn"
                onClick={handleSwitchToCheckIn}
              >
                <i className="pi pi-pencil" />
                {t('statusPopover.checkIn.checkInButton', 'Check In')}
              </button>
            )}

            <h5 className="status-factor-panel__section-title">
              {t('statusPopover.history.title', 'Check-In History')}
            </h5>

            {historyLoading && (
              <div className="status-factor-panel__history-loading">
                <i className="pi pi-spin pi-spinner" />
                <span>{t('statusPopover.history.loading', 'Loading history...')}</span>
              </div>
            )}

            {historyError && (
              <div className="status-factor-panel__history-error">
                <i className="pi pi-exclamation-triangle" />
                <span>{historyError}</span>
              </div>
            )}

            {!historyLoading && !historyError && checkInHistory.length === 0 && (
              <div className="status-factor-panel__history-empty">
                <i className="pi pi-inbox" />
                <span>{t('statusPopover.history.empty', 'No check-ins yet')}</span>
              </div>
            )}

            {!historyLoading && !historyError && checkInHistory.length > 0 && (
              <ul className="status-factor-panel__history-list">
                {checkInHistory.map((checkIn) => {
                  const statusOption = getStatusOption(checkIn.statusValue);
                  const statusClass = `status-history-item__status--${checkIn.statusValue.replaceAll('_', '-')}`;

                  return (
                    <li key={checkIn.id} className="status-history-item" data-testid={`history-item-${checkIn.id}`}>
                      <div className="status-history-item__header">
                        <div className={`status-history-item__status ${statusClass}`}>
                          {statusOption && (
                            <i className={statusOption.icon} style={{ color: statusOption.color }} />
                          )}
                          <span>{statusOption?.label || checkIn.statusValue}</span>
                        </div>
                        <span className="status-history-item__time" title={new Date(checkIn.submittedAt).toLocaleString()}>
                          {formatRelativeTime(checkIn.submittedAt)}
                        </span>
                      </div>
                      {checkIn.submittedBy && (
                        <div className="status-history-item__submitter">
                          <AtlasSphereIdentityMolecule
                            sphereId={checkIn.submittedBy}
                            className="status-history-item__identity"
                          />
                        </div>
                      )}
                      {checkIn.comment && (
                        <p className="status-history-item__comment">{checkIn.comment}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* View-only message for non-manual factors */}
        {!isManual && (
          <div className="status-factor-panel__info">
            <p className="status-factor-panel__info-text">
              {t(
                'statusPopover.panel.autoCalculated',
                'This factor is automatically calculated based on related data.'
              )}
            </p>
          </div>
        )}

        {/* No permission message (only show in checkin mode or if no history to show) */}
        {isManual && !hasEditPermission && currentView === 'checkin' && (
          <div className="status-factor-panel__info">
            <p className="status-factor-panel__info-text">
              {t(
                'statusPopover.panel.noEditPermission',
                'You do not have permission to submit check-ins for this factor.'
              )}
            </p>
          </div>
        )}
      </div>

      {/* Panel Footer */}
      <div className="status-factor-panel__footer">
        <small>{t('statusPopover.panel.footerHint', 'Check-ins update the factor status')}</small>
      </div>
    </div>
  );
};

export default StatusFactorPanel;
